import { type DocumentDetails, useGetDocument } from 'entities/document/api';
import { getHasHateoasAccess } from 'helpers';

export type UseDocumentCapabilityProps = {
  /**
   * The ID of the document to be managed by the hook.
   * This is used to fetch the specific document data.
   */
  documentId?: number;

  /**
   * Document data to be managed by the hook.
   * This can be used instead of fetching the document by ID.
   */
  document?: DocumentDetails;
};

/**
 * Hook to manage document capabilities.
 *
 * @param props - The properties for the hook, including the document ID and document data.
 */
export const useDocumentCapability = (props: UseDocumentCapabilityProps) => {
  /**
   * Hook to fetch a specific document by its ID.
   */
  const { data, isLoading, isError } = useGetDocument(props.documentId);

  /**
   * Document to be used for capability checks.
   */
  const innerDocument = props.document || data;

  return {
    /**
     * Boolean indicating if the document is loading.
     */
    isLoading: innerDocument ? false : isLoading,

    /**
     * Boolean indicating the document fetch failed and left no data to derive
     * capabilities from. Mirrors `isLoading`: once a document is in hand (from
     * `props.document` or a successful fetch) there is no error. Consumers use
     * this to tell "capability not granted" apart from "capability unknown
     * because the fetch failed" — see `useDocumentQueueGroupsAccess`.
     */
    isError: innerDocument ? false : isError,

    /**
     * User can archive the document.
     */
    archive: getHasHateoasAccess('archive', innerDocument?.__links),

    /**
     * User can delete the document.
     */
    delete: getHasHateoasAccess('delete', innerDocument?.__links),

    /**
     * (to check)
     */
    send: getHasHateoasAccess('send', innerDocument?.__links),

    /**
     * User can download pdf to sign in an external application.
     */
    externalSignaturePrepare: getHasHateoasAccess(
      'externalSignaturePrepare',
      innerDocument?.__links,
    ),

    /**
     * User can upload a signature from an external application.
     */
    externalSignatureUpload: getHasHateoasAccess(
      'externalSignatureUpload',
      innerDocument?.__links,
    ),

    /**
     * User can update signature position.
     */
    updatePosition: getHasHateoasAccess(
      'updatePosition',
      innerDocument?.__links,
    ),

    /**
     * User can sign the document.
     */
    sign:
      getHasHateoasAccess('sign', innerDocument?.__links) &&
      innerDocument?.stage === 'in_sign',

    accept:
      getHasHateoasAccess('accept', innerDocument?.__links) ||
      (getHasHateoasAccess('sign', innerDocument?.__links) &&
        innerDocument?.stage === 'in_acceptance'),

    /**
     * User can reject the document.
     */
    reject: getHasHateoasAccess('reject', innerDocument?.__links),

    /**
     * User can update the document.
     */
    update: getHasHateoasAccess('update', innerDocument?.__links),

    /**
     * User can see the document versions.
     */
    versions: getHasHateoasAccess('versions', innerDocument?.__links),

    /**
     * User can download document in PDF
     */
    pdf:
      getHasHateoasAccess('pdf', innerDocument?.__links) ||
      innerDocument?.stage !== 'new',

    /**
     * User can duplicate the document.
     */
    duplicate: getHasHateoasAccess('duplicate', innerDocument?.__links),

    /**
     * User can manage managers of the document.
     */
    manageManagers: getHasHateoasAccess('userRoles', innerDocument?.__links),

    /**
     * User can manage notes of the document.
     */
    note: getHasHateoasAccess('note', innerDocument?.__links),

    /**
     * User can see pages of the document.
     */
    pages: getHasHateoasAccess('pages', innerDocument?.__links),

    /**
     * User can unreject the document.
     */
    unreject: getHasHateoasAccess('unreject', innerDocument?.__links),

    /**
     * User can manage queue.
     */
    queue: getHasHateoasAccess('queue', innerDocument?.__links),

    /**
     * Whether queue groups are available for this document. Document-scoped
     * counterpart of the board-scoped `useHasQueueGroupsAccess`; both are gated
     * by the same `FeatureType::QueueGroups` on the organisation. Use this one
     * inside the document modal; use the board-scoped hook when there is no
     * document in context (palette, default-queue, package designers).
     */
    queueGroups: getHasHateoasAccess('queueGroups', innerDocument?.__links),

    /**
     * User can see queue group templates (to check)
     */
    queueGroupTemplates: getHasHateoasAccess(
      'queueGroupTemplates',
      innerDocument?.__links,
    ),

    /**
     * User can resume queue.
     */
    queueResume: getHasHateoasAccess('queueResume', innerDocument?.__links),

    /**
     * (to check)
     */
    pwpwToken: getHasHateoasAccess('pwpwToken', innerDocument?.__links),
  };
};

export type DocumentCapability = ReturnType<typeof useDocumentCapability>;
